import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Input,
  Loader,
  ScrollArea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
  useDebounceCallback,
} from "@devhop/ui";
import { Check, Loader2, Search, Table } from "lucide-react";
import { useState } from "react";
import {
  useAddOrderItems,
  useBranchTables,
  useLatestShift,
  useOrders,
} from "../api/queries";
import type { CartItem } from "../context/CartContext";
import { usePOSContext } from "../context/POSContext";
import { printSupplementaryBillAndKitchen } from "../lib/print-order-bill";

type AddToOrderDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cartItems: CartItem[];
  onSuccess?: () => void;
};

export function AddToOrderDialog({
  open,
  onOpenChange,
  cartItems,
  onSuccess,
}: AddToOrderDialogProps) {
  const { selection } = usePOSContext();
  const branchId = selection?.branchId ?? null;
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTableId, setSelectedTableId] = useState<string | null>(null);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const addOrderItemsMutation = useAddOrderItems();

  const debouncedSearch = useDebounceCallback(
    (value: string) => setSearchQuery(value),
    300,
  );

  // Get latest shift for current shift filter
  const { data: latestShift } = useLatestShift(branchId);

  // Get branch tables for filter dropdown
  const { data: tables = [] } = useBranchTables(branchId);

  // Get orders for current shift
  const { data: ordersData, isLoading } = useOrders(
    branchId,
    {
      status: ["OPEN"],
      search: searchQuery || undefined,
      shiftId: latestShift?.id,
      tableId: selectedTableId || undefined,
    },
    { limit: 50, offset: 0 },
    { field: "createdAt", order: "desc" },
  );

  const orders = ordersData?.data ?? [];

  const handleSelect = (orderId: string) => {
    setSelectedOrderId(orderId);
  };

  const handleSubmit = async () => {
    if (!branchId) {
      toast.error("ກະລຸນາເລືອກສາຂາ");
      return;
    }

    if (!selectedOrderId) {
      toast.error("ກະລຸນາເລືອກ Order");
      return;
    }

    if (cartItems.length === 0) {
      toast.error("ກະຕ່າຫວ່າງ");
      return;
    }

    try {
      const result = await addOrderItemsMutation.mutateAsync({
        orderId: selectedOrderId,
        branchId,
        cartItems,
      });

      // พิมพ์ Supplementary Bill (เฉพาะรายการที่สั่งเพิ่ม) + Kitchen tickets ของรายการเพิ่ม
      const newOrderItemIds = result.items
        .slice(-cartItems.length)
        .map((item) => item.id);
      if (newOrderItemIds.length > 0) {
        try {
          await printSupplementaryBillAndKitchen(
            selectedOrderId,
            branchId,
            newOrderItemIds,
          );
        } catch (printErr) {
          toast.error(
            printErr instanceof Error ? printErr.message : "ເກີດຂໍ້ຜິດພາດໃນການພິມ",
          );
        }
      }

      toast.success("ເພີ່ມສິນຄ້າເຂົ້າ Order ສຳເລັດແລ້ວ");
      setSelectedOrderId(null);
      setSearchQuery("");
      setSelectedTableId(null);
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "ເກີດຂໍ້ຜິດພາດໃນການເພີ່ມສິນຄ້າ",
      );
    }
  };

  const handleClose = () => {
    if (addOrderItemsMutation.isPending) return;
    setSelectedOrderId(null);
    setSearchQuery("");
    setSelectedTableId(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>ເພີ່ມສິນຄ້າເຂົ້າ Order</DialogTitle>
          <DialogDescription>ເລືອກ order ທີ່ຕ້ອງການເພີ່ມສິນຄ້າ</DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[400px]">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              {/* Table filter */}
              <Select
                value={selectedTableId ?? "all"}
                onValueChange={(value) =>
                  setSelectedTableId(value === "all" ? null : value)
                }
              >
                <SelectTrigger>
                  <Table className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="ເລືອກໂຕະ" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">ທັງໝົດ</SelectItem>
                  {tables.map((table) => (
                    <SelectItem key={table.id} value={table.id}>
                      {table.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Search input */}
              <div className="relative flex-1">
                <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="ຄົ້ນຫາ Order..."
                  className="pl-9"
                  onChange={(e) => debouncedSearch(e.target.value)}
                />
              </div>
            </div>

            {/* Orders list */}
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader />
              </div>
            ) : orders.length === 0 ? (
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <Search className="h-8 w-8 text-muted-foreground" />
                  </EmptyMedia>
                  <EmptyTitle>ບໍ່ພົບ Order</EmptyTitle>
                  <EmptyDescription>ບໍ່ມີ order ທີ່ສາມາດເພີ່ມສິນຄ້າໄດ້</EmptyDescription>
                </EmptyHeader>
              </Empty>
            ) : (
              <div className="space-y-2">
                {orders.map((order) => (
                  <button
                    key={order.id}
                    type="button"
                    onClick={() => handleSelect(order.id)}
                    className={cn(
                      "flex w-full items-center justify-between rounded-lg border p-3 text-left transition-colors hover:bg-accent",
                      selectedOrderId === order.id &&
                        "border-primary bg-accent",
                    )}
                  >
                    <div className="flex-1">
                      <p className="font-medium">Order #{order.orderNo}</p>
                      <div className="mt-1 flex items-center gap-2 text-muted-foreground text-xs">
                        {order.tableName && <span>ໂຕະ: {order.tableName}</span>}
                        {order.tableName && <span>•</span>}
                        <span>
                          {Number(order.grandTotal).toLocaleString("lo-LA")} ₭
                        </span>
                      </div>
                    </div>
                    {selectedOrderId === order.id && (
                      <Check className="h-4 w-4 text-primary" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={addOrderItemsMutation.isPending}
          >
            ຍົກເລີກ
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              addOrderItemsMutation.isPending ||
              !selectedOrderId ||
              cartItems.length === 0
            }
          >
            {addOrderItemsMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ກຳລັງເພີ່ມ...
              </>
            ) : (
              "ເພີ່ມສິນຄ້າ"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
